import React, { useState, useEffect } from 'react';
import {
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    RadialBarChart,
    RadialBar,
} from 'recharts';
import SidebarLayout from '../components/SidebarLayout';
import fileService from '../services/fileService';
import {
    HardDrive,
    FileText,
    Image,
    Film,
    Music,
    Archive,
    TrendingUp,
    Database,
    Zap,
} from 'lucide-react';

// ── responsive hook ──
const useWindowWidth = () => {
    const [width, setWidth] = useState(window.innerWidth);
    useEffect(() => {
        const fn = () => setWidth(window.innerWidth);
        window.addEventListener('resize', fn);
        return () => window.removeEventListener('resize', fn);
    }, []);
    return width;
};

// ────────────────── helpers ──────────────────
const fmtBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const fmtPct = (val) => `${parseFloat(val).toFixed(1)}%`;

const PIE_COLORS = ['#38bdf8', '#818cf8', '#34d399', '#f59e0b', '#64748b'];
const TYPE_COLORS = {
    image: '#38bdf8',
    video: '#818cf8',
    document: '#34d399',
    audio: '#f59e0b',
    other: '#64748b',
};

// ────────────────── custom tooltip ──────────────────
const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div style={{
            background: 'rgba(15,23,42,0.95)',
            border: '1px solid rgba(56,189,248,0.2)',
            borderRadius: '10px',
            padding: '10px 16px',
            color: '#e2e8f0',
            fontSize: '13px',
            backdropFilter: 'blur(8px)',
        }}>
            {label && <p style={{ color: '#94a3b8', marginBottom: 4 }}>{label}</p>}
            {payload.map((entry, i) => (
                <p key={i} style={{ color: entry.color || '#38bdf8', margin: '2px 0' }}>
                    <strong>{entry.name}:</strong> {typeof entry.value === 'number' && entry.value > 1024
                        ? fmtBytes(entry.value)
                        : entry.value}
                </p>
            ))}
        </div>
    );
};

// ────────────────── Stat Card ──────────────────
const StatCard = ({ icon: Icon, label, value, sub, color }) => (
    <div style={{
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '16px',
        padding: '20px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        cursor: 'default',
    }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; }}
    >
        <div style={{ background: `${color}18`, borderRadius: '12px', padding: '12px', flexShrink: 0 }}>
            <Icon size={22} color={color} />
        </div>
        <div>
            <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>{label}</div>
            <div style={{ color: '#1e293b', fontSize: '20px', fontWeight: 700 }}>{value}</div>
            {sub && <div style={{ color: '#94a3b8', fontSize: '12px', marginTop: '2px' }}>{sub}</div>}
        </div>
    </div>
);

// ────────────────── Section wrapper ──────────────────
const ChartCard = ({ title, children }) => (
    <div style={{
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    }}>
        <h3 style={{ color: '#1e293b', fontWeight: 600, fontSize: '15px', marginTop: 0, marginBottom: '20px' }}>{title}</h3>
        {children}
    </div>
);

// ────────────────── Main Component ──────────────────
const StorageDashboard = () => {
    const width = useWindowWidth();
    const isMobile = width < 768;
    const isTablet = width < 1024;
    const [storageData, setStorageData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            try {
                const res = await fileService.getStorageStats();
                setStorageData(res.storage || res);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const storage = storageData || {};
    const pct = storage.usagePercentage || 0;
    const used = storage.used || 0;
    const limit = storage.limit || 1;
    const available = storage.available || 0;
    const filesCount = storage.files_count || 0;
    const breakdown = storage.breakdown || {};

    // ── Data for pie chart ──
    const pieData = [
        { name: 'Gambar', value: breakdown.image || 0, color: TYPE_COLORS.image, icon: Image },
        { name: 'Video', value: breakdown.video || 0, color: TYPE_COLORS.video, icon: Film },
        { name: 'Dokumen', value: breakdown.document || 0, color: TYPE_COLORS.document, icon: FileText },
        { name: 'Audio', value: breakdown.audio || 0, color: TYPE_COLORS.audio, icon: Music },
        { name: 'Lainnya', value: breakdown.other || 0, color: TYPE_COLORS.other, icon: Archive },
    ].filter(d => d.value > 0);

    // ── Bar chart data ──
    const barData = pieData.map(d => ({
        name: d.name,
        Ukuran: d.value,
        fill: d.color,
    }));

    // ── Radial gauge ──
    const radialData = [
        { name: 'Digunakan', value: pct, fill: pct > 85 ? '#ef4444' : pct > 60 ? '#f59e0b' : '#38bdf8' },
    ];

    const pctColor = pct > 85 ? '#ef4444' : pct > 60 ? '#f59e0b' : '#10b981';

    if (loading) {
        return (
            <SidebarLayout>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8' }}>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ marginBottom: 12, fontSize: 32 }}>⏳</div>
                        <div>Memuat data penyimpanan…</div>
                    </div>
                </div>
            </SidebarLayout>
        );
    }

    return (
        <SidebarLayout storageData={{ storage }}>
            <div style={{ padding: isMobile ? '12px' : '28px', maxWidth: '1200px', margin: '0 auto' }}>
                {/* ── Page header ── */}
                <div style={{ marginBottom: '28px' }}>
                    <h1 style={{
                        color: '#1e293b', fontWeight: 800, fontSize: '26px', margin: 0,
                        display: 'flex', alignItems: 'center', gap: '10px',
                    }}>
                        <div style={{ background: 'linear-gradient(135deg,#38bdf8,#818cf8)', borderRadius: '10px', padding: '6px' }}>
                            <TrendingUp size={22} color="#fff" />
                        </div>
                        Dashboard Penyimpanan
                    </h1>
                    <p style={{ color: '#94a3b8', margin: '6px 0 0', fontSize: '14px' }}>
                        Ikhtisar penggunaan dan statistik file Anda
                    </p>
                </div>

                {/* ── Stat Cards ── */}
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit,minmax(${isMobile ? '140px' : '200px'},1fr))`, gap: isMobile ? '10px' : '16px', marginBottom: isMobile ? '16px' : '28px' }}>
                    <StatCard icon={HardDrive} label="Total Digunakan" value={fmtBytes(used)} sub={`${fmtPct(pct)} dari kapasitas`} color="#38bdf8" />
                    <StatCard icon={Database} label="Kapasitas" value={fmtBytes(limit)} sub="Total kuota" color="#818cf8" />
                    <StatCard icon={Zap} label="Tersedia" value={fmtBytes(available)} sub="Ruang kosong" color="#34d399" />
                    <StatCard icon={Archive} label="Total File" value={filesCount.toLocaleString()} sub="Semua jenis" color="#f59e0b" />
                </div>

                {/* ── Row 1: Radial + Pie ── */}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                    {/* Radial gauge */}
                    <ChartCard title="Penggunaan Penyimpanan">
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div style={{ position: 'relative', width: 220, height: 220 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <RadialBarChart
                                        cx="50%" cy="50%"
                                        innerRadius="65%"
                                        outerRadius="95%"
                                        startAngle={220}
                                        endAngle={-40}
                                        data={radialData}
                                        barSize={18}
                                    >
                                        <RadialBar
                                            dataKey="value"
                                            cornerRadius={10}
                                            background={{ fill: '#f1f5f9' }}
                                        />
                                    </RadialBarChart>
                                </ResponsiveContainer>
                                <div style={{
                                    position: 'absolute', inset: 0,
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <div style={{ color: pctColor, fontWeight: 800, fontSize: '28px', lineHeight: 1 }}>
                                        {fmtPct(pct)}
                                    </div>
                                    <div style={{ color: '#94a3b8', fontSize: '12px', marginTop: 4 }}>terpakai</div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '24px', marginTop: '12px' }}>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ color: '#0ea5e9', fontWeight: 700 }}>{fmtBytes(used)}</div>
                                    <div style={{ color: '#94a3b8', fontSize: '11px' }}>Digunakan</div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ color: '#10b981', fontWeight: 700 }}>{fmtBytes(available)}</div>
                                    <div style={{ color: '#94a3b8', fontSize: '11px' }}>Tersedia</div>
                                </div>
                            </div>
                        </div>
                    </ChartCard>

                    {/* Pie chart */}
                    <ChartCard title="Distribusi Jenis File">
                        {pieData.length === 0 ? (
                            <div style={{ textAlign: 'center', color: '#94a3b8', padding: '40px 0' }}>Belum ada file</div>
                        ) : (
                            <>
                                <ResponsiveContainer width="100%" height={200}>
                                    <PieChart>
                                        <Pie
                                            data={pieData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={55}
                                            outerRadius={90}
                                            paddingAngle={3}
                                            dataKey="value"
                                            animationBegin={0}
                                            animationDuration={800}
                                        >
                                            {pieData.map((entry, i) => (
                                                <Cell key={i} fill={entry.color} stroke="rgba(0,0,0,0.2)" strokeWidth={1} />
                                            ))}
                                        </Pie>
                                        <Tooltip content={<ChartTooltip />} />
                                    </PieChart>
                                </ResponsiveContainer>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                                    {pieData.map((d, i) => (
                                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <div style={{ width: 10, height: 10, borderRadius: '50%', background: d.color, flexShrink: 0 }} />
                                            <span style={{ color: '#64748b', fontSize: '12px' }}>{d.name} ({fmtBytes(d.value)})</span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </ChartCard>
                </div>

                {/* ── Row 2: Bar chart ── */}
                <ChartCard title="Penggunaan Berdasarkan Jenis File">
                    {barData.length === 0 ? (
                        <div style={{ textAlign: 'center', color: '#94a3b8', padding: '40px 0' }}>Belum ada file untuk ditampilkan</div>
                    ) : (
                        <ResponsiveContainer width="100%" height={240}>
                            <BarChart data={barData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis dataKey="name" stroke="#cbd5e1" tick={{ fill: '#64748b', fontSize: 12 }} />
                                <YAxis
                                    stroke="#cbd5e1"
                                    tick={{ fill: '#64748b', fontSize: 11 }}
                                    tickFormatter={(v) => {
                                        if (v >= 1024 ** 3) return `${(v / 1024 ** 3).toFixed(1)}GB`;
                                        if (v >= 1024 ** 2) return `${(v / 1024 ** 2).toFixed(0)}MB`;
                                        if (v >= 1024) return `${(v / 1024).toFixed(0)}KB`;
                                        return `${v}B`;
                                    }}
                                />
                                <Tooltip content={<ChartTooltip />} />
                                <Bar dataKey="Ukuran" radius={[6, 6, 0, 0]} animationDuration={800}>
                                    {barData.map((entry, i) => (
                                        <Cell key={i} fill={entry.fill} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>

                {/* ── Row 3: File type breakdown cards ── */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: '12px', marginTop: '20px' }}>
                    {[
                        { label: 'Gambar', value: breakdown.image || 0, icon: Image, color: TYPE_COLORS.image },
                        { label: 'Video', value: breakdown.video || 0, icon: Film, color: TYPE_COLORS.video },
                        { label: 'Dokumen', value: breakdown.document || 0, icon: FileText, color: TYPE_COLORS.document },
                        { label: 'Audio', value: breakdown.audio || 0, icon: Music, color: TYPE_COLORS.audio },
                        { label: 'Lainnya', value: breakdown.other || 0, icon: Archive, color: TYPE_COLORS.other },
                    ].map(({ label, value, icon: Icon, color }) => (
                        <div key={label} style={{
                            background: '#ffffff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '12px',
                            padding: '16px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Icon size={16} color={color} />
                                <span style={{ color: '#64748b', fontSize: '12px', fontWeight: 500 }}>{label}</span>
                            </div>
                            <div style={{ color, fontSize: '18px', fontWeight: 700 }}>{fmtBytes(value)}</div>
                            <div style={{
                                height: '4px',
                                background: '#f1f5f9',
                                borderRadius: '2px',
                                overflow: 'hidden',
                            }}>
                                <div style={{
                                    height: '100%',
                                    width: `${used > 0 ? Math.min((value / used) * 100, 100) : 0}%`,
                                    background: color,
                                    borderRadius: '2px',
                                    transition: 'width 0.8s ease',
                                }} />
                            </div>
                            <div style={{ color: '#94a3b8', fontSize: '11px' }}>
                                {used > 0 ? `${((value / used) * 100).toFixed(1)}% dari pemakaian` : '—'}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </SidebarLayout>
    );
};

export default StorageDashboard;
